import { TriangleAlert, Trash2 } from 'lucide-preact'
import { useEffect, useRef } from 'preact/hooks'
import { raw, text } from '#contract'
import type { SaveSlotView } from '#contract'
import { useClient } from '../../app/client-context.ts'
import { useView } from '../../app/use-view.ts'
import { playTimeText, savedAtText } from '../../i18n/dates.ts'
import { useLocale, useT } from '../../i18n/index.ts'
import { formatMoney } from '../../i18n/money.ts'
import styles from './save-slots.module.css'
import { savedWhen } from './slot-text.ts'

interface SlotCardProps {
  readonly slot: SaveSlotView
  /** Load mode: only a save that reads can be chosen. */
  readonly disabled: boolean
  /** Focuses the card on mount, so keyboard and gamepad users start there. */
  readonly autoFocus: boolean
  readonly onChoose: () => void
  /** Absent for an empty slot. */
  readonly onDelete?: () => void
}

/** One save slot: what's in it (when, play time, the date saved), or Empty, or Damaged. */
export function SlotCard({ slot, disabled, autoFocus, onChoose, onDelete }: SlotCardProps) {
  const t = useT()
  const locale = useLocale()
  const client = useClient()
  const ref = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (autoFocus) ref.current?.focus()
  }, [autoFocus])

  const name = t(text('saves.slot', { n: slot.slot }))
  return (
    <li class={styles.card}>
      <button
        ref={ref}
        type="button"
        class={styles.choose}
        disabled={disabled}
        onClick={() => {
          onChoose()
          client?.playUiSound('click')
        }}
      >
        <span class={styles.name}>
          {name}
          {slot.state === 'damaged' && (
            <span class={styles.damaged}>
              <TriangleAlert class={styles.icon} aria-hidden="true" />
              {t(text('saves.damaged'))}
            </span>
          )}
        </span>
        <SlotSummary slot={slot} locale={locale} />
      </button>
      {onDelete && (
        <button
          type="button"
          class={styles.bin}
          aria-label={t(text('saves.deleteSlot', { n: slot.slot }))}
          title={t(text('saves.deleteSlot', { n: slot.slot }))}
          onClick={() => {
            onDelete()
            client?.playUiSound('click')
          }}
        >
          <Trash2 class={styles.icon} aria-hidden="true" />
        </button>
      )}
    </li>
  )
}

function SlotSummary({ slot, locale }: { slot: SaveSlotView; locale: string }) {
  const t = useT()
  // The menu's level is loaded, so the wallet's view is there to name the currency.
  const currency = useView((view) => view.wallet?.currency ?? null)
  if (slot.state !== 'ok') {
    const hint = slot.state === 'empty' ? 'saves.empty' : 'saves.damagedHint'
    return <span class={styles.details}>{t(text(hint))}</span>
  }
  const day = savedWhen(locale, slot)
  const money = currency === null ? null : formatMoney(slot.cents, currency, locale)
  const when =
    money === null ? day : day ? text('saves.whenMoney', { when: day, money }) : raw(money)
  const details = text('saves.details', {
    played: playTimeText(slot.playSeconds),
    savedAt: savedAtText(locale, slot.savedAt),
  })
  return (
    <>
      {when && <span class={styles.when}>{t(when)}</span>}
      <span class={styles.details}>{t(details)}</span>
    </>
  )
}
